import { zodResolver } from "@hookform/resolvers/zod";
import * as Form from "@radix-ui/react-form";
import {
  Button,
  Checkbox,
  Flex,
  Heading,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useForm, Controller, UseFormReturn } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import { FiChevronLeft } from "react-icons/fi";

import ControlledInputField from "@/components/ui/form/ControlledInputField";
import PasswordInputToggleField from "@/components/ui/form/PasswordInputToggleField";
import PasswordRequirementsChecklist from "@/components/auth/PasswordRequirementsChecklist";
import { initiateOtcAuth } from "@/lib/auth";
import { getLastAuth } from "@/lib/storage-utils";
import { useLogin, useSignUp } from "@/components/providers/ApiProvider/hooks";
import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { SignUpDto } from "@/lib/lutely-api/gen/dtos";
import { useAuthControllerCheckEmail } from "@/lib/lutely-api/gen/auth/auth";
import SocialAuthButtons from "./SocialAuthButtons";

// ─── Schemas ───

const identifySchema = z.object({
  email: z.string().email({ message: "Please enter a valid email." }),
});

const passwordSchema = z.object({
  password: z.string().min(1, { message: "Please enter your password." }),
});

const signUpSchema = z
  .object({
    email: z.string().email({ message: "Please enter a valid email." }),
    password: z
      .string()
      .min(8, { message: "Password does not meet requirements." })
      .regex(/[A-Z]/, { message: "Password does not meet requirements." })
      .regex(/[a-z]/, { message: "Password does not meet requirements." })
      .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, {
        message: "Password does not meet requirements.",
      })
      .regex(/\d/, { message: "Password does not meet requirements." }),
    confirm: z.string(),
    termsAgreement: z.boolean().refine((val) => val === true, {
      message: "You must agree to the terms and privacy policy to sign up.",
    }),
  })
  .refine((data) => data.password === data.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

// ─── Types ───

type IdentifyFormValues = z.infer<typeof identifySchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;
type SignUpFormValues = z.infer<typeof signUpSchema>;

type Phase = "email" | "password" | "signup";

type IdentifyFormProps = {
  onCodePath: (email: string, authMethods?: string[]) => void;
  onSuccess: () => void;
  onForgotPassword: (email: string) => void;
  initialEmail?: string;
  initialPhase?: "signup" | "password";
  compact?: boolean;
  hideHeading?: boolean;
  codeFirst?: boolean;
};

// ─── Responsive heading class ───
const headingClass = (compact?: boolean) =>
  `font-figtree text-blue-900 leading-[1.1] ${compact ? "text-[28px] tracking-[-0.28px]" : "text-[24px] tracking-[-0.24px] font-semibold sm:text-[32px] sm:tracking-[-0.32px] sm:font-bold md:text-[40px] md:tracking-[-0.4px]"}`;

// ─── Signup Phase ───

function SignupPhase({
  signUpForm,
  onSubmit,
  onCodePath,
  onBack,
  compact,
  hideHeading,
  codeLoading,
  signUpPassword,
  showRequirements,
  termsAccepted,
  onPasswordFieldFocus,
}: Readonly<{
  signUpForm: UseFormReturn<SignUpFormValues>;
  onSubmit: (data: SignUpFormValues) => Promise<void>;
  onCodePath: () => void;
  onBack: () => void;
  compact?: boolean;
  hideHeading?: boolean;
  codeLoading: boolean;
  signUpPassword: string;
  showRequirements: boolean;
  termsAccepted: boolean;
  onPasswordFieldFocus: () => void;
}>) {
  return (
    <Flex
      className={`w-full ${compact ? "gap-4" : "gap-4 sm:gap-6"}`}
      direction="column"
      aria-labelledby="identify-form-title"
    >
      {hideHeading ? (
        <button
          type="button"
          onClick={onBack}
          className="self-start cursor-pointer text-blue-900 hover:text-blue-600"
          aria-label="Go back"
        >
          <FiChevronLeft className="w-5 h-5" />
        </button>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={onBack}
            className="absolute left-0 top-1 cursor-pointer text-blue-900 hover:text-blue-600"
            aria-label="Go back"
          >
            <FiChevronLeft className="w-5 h-5" />
          </button>
        </div>
      )}

      {!hideHeading && (
        <div className="space-y-2 text-center">
          <Heading
            id="identify-form-title"
            as="h2"
            className={headingClass(compact)}
          >
            Create your account
          </Heading>
        </div>
      )}

      <Form.Root
        className={`flex flex-col ${compact ? "gap-3" : "gap-3 sm:gap-4"}`}
        onSubmit={signUpForm.handleSubmit(onSubmit)}
        noValidate
      >
        <div className="space-y-3">
          <ControlledInputField
            name="email"
            control={signUpForm.control}
            label="Email"
            type="email"
            placeholder="Email"
            required
            autoComplete="username"
          />

          <Flex direction="column" gap="2" onFocus={onPasswordFieldFocus}>
            <Text size="2" className="text-gray-8">
              Password
            </Text>
            <PasswordInputToggleField
              control={signUpForm.control}
              name="password"
              label="Password"
              required
              autoComplete="new-password"
              data-testid="auth-signup-password-input"
            />
            <PasswordInputToggleField
              control={signUpForm.control}
              name="confirm"
              label="Confirm Password"
              required
              confirm
              autoComplete="new-password"
              data-testid="auth-signup-confirm-input"
            />
            <div
              className="grid transition-[grid-template-rows] duration-200 ease-out"
              style={{
                gridTemplateRows: showRequirements ? "1fr" : "0fr",
              }}
            >
              <div className="overflow-hidden">
                <Flex direction="column" gap="2" mt="1">
                  <Text size="2" className="text-blue-900">
                    Your password must contain at least:
                  </Text>
                  <PasswordRequirementsChecklist password={signUpPassword} />
                </Flex>
              </div>
            </div>
          </Flex>
        </div>

        <Controller
          control={signUpForm.control}
          name="termsAgreement"
          render={({ field, fieldState }) => (
            <div className="space-y-1">
              <label
                htmlFor="termsAgreement"
                className="flex items-start gap-3 text-[13px] sm:text-[14px] text-blue-900 leading-[1.4] min-h-[44px]"
              >
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  id="termsAgreement"
                  className="mt-[2px]"
                  data-testid="auth-signup-terms-checkbox"
                />
                <span>
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    className="font-semibold text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </Link>{" "}
                  and the{" "}
                  <Link
                    to="/privacy"
                    className="font-semibold text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {fieldState.error && (
                <Text color="red" size="1">
                  {fieldState.error.message}
                </Text>
              )}
            </div>
          )}
        />

        <Flex className="gap-4 sm:gap-6" direction="column">
          <Form.Submit asChild>
            <Button
              loading={signUpForm.formState.isSubmitting}
              type="submit"
              radius="full"
              disabled={signUpForm.formState.isSubmitting || !termsAccepted}
              className="h-[40px]"
              data-testid="auth-signup-submit-button"
            >
              Sign Up
            </Button>
          </Form.Submit>

          <Flex direction="column" align="center" gap="2">
            <button
              type="button"
              onClick={onCodePath}
              disabled={codeLoading || !termsAccepted}
              className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {codeLoading ? "Sending code..." : "Continue with code instead"}
            </button>
          </Flex>
        </Flex>
      </Form.Root>
    </Flex>
  );
}

// ─── Password Phase ───

function PasswordPhase({
  email,
  passwordForm,
  onSubmit,
  onCodePath,
  onBack,
  onForgotPassword,
  compact,
  hideHeading,
  codeLoading,
}: Readonly<{
  email: string;
  passwordForm: UseFormReturn<PasswordFormValues>;
  onSubmit: (data: PasswordFormValues) => Promise<void>;
  onCodePath: () => void;
  onBack: () => void;
  onForgotPassword: () => void;
  compact?: boolean;
  hideHeading?: boolean;
  codeLoading: boolean;
}>) {
  return (
    <Flex
      className={`w-full ${compact ? "gap-4" : "gap-4 sm:gap-6 md:gap-8"}`}
      direction="column"
      aria-labelledby="identify-form-title"
    >
      {!hideHeading && (
        <div className="space-y-2 text-center">
          <Heading
            id="identify-form-title"
            as="h2"
            className={headingClass(compact)}
          >
            Welcome to Lutely
          </Heading>
        </div>
      )}

      <Form.Root
        className={`flex flex-col ${compact ? "gap-3" : "gap-4 sm:gap-6"}`}
        onSubmit={passwordForm.handleSubmit(onSubmit)}
        noValidate
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Text size="2" className="text-gray-9">
              Email
            </Text>
            <TextField.Root
              value={email}
              disabled
              name="email"
              autoComplete="username"
              className="cursor-default [&_input]:cursor-default"
            />
          </div>

          <div className="space-y-1.5">
            <Text size="2" className="text-gray-9">
              Password
            </Text>
            <PasswordInputToggleField
              control={passwordForm.control}
              name="password"
              label="Password"
              required
              autoComplete="current-password"
            />
          </div>
        </div>

        <Form.Submit asChild>
          <Button
            loading={passwordForm.formState.isSubmitting}
            type="submit"
            radius="full"
            className="h-[40px]"
          >
            Sign in
          </Button>
        </Form.Submit>

        <div className="text-center">
          <button
            type="button"
            onClick={onCodePath}
            disabled={codeLoading}
            className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
          >
            {codeLoading ? "Sending code..." : "Continue with code instead"}
          </button>
        </div>

        <Separator className="w-full bg-gray-6" />

        <Flex justify="between" align="center">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
          >
            &larr; Use a different email
          </button>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
          >
            Forgot password?
          </button>
        </Flex>
      </Form.Root>
    </Flex>
  );
}

// ─── Main Component ───

export function IdentifyForm({
  onCodePath,
  onSuccess,
  onForgotPassword,
  initialEmail,
  initialPhase,
  compact,
  hideHeading,
  codeFirst,
}: Readonly<IdentifyFormProps>) {
  const [phase, setPhase] = useState<Phase>(
    initialPhase ?? (initialEmail ? "password" : "email"),
  );
  const [email, setEmail] = useState(initialEmail ?? "");
  const [codeLoading, setCodeLoading] = useState(false);
  const [passwordFieldActive, setPasswordFieldActive] = useState(false);

  const lastAuth = useMemo(() => getLastAuth(), []);
  const isAppleRelay = lastAuth?.email?.includes("privaterelay.appleid.com");
  const prefillEmail =
    lastAuth?.email &&
    !isAppleRelay &&
    lastAuth?.method !== "google" &&
    lastAuth?.method !== "apple"
      ? lastAuth.email
      : "";

  const loginUser = useLogin();
  const signUpUser = useSignUp();
  const { mutateAsync: checkEmailMutation } = useAuthControllerCheckEmail();

  const emailForm = useForm<IdentifyFormValues>({
    resolver: zodResolver(identifySchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { email: prefillEmail },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { password: "" },
  });

  const signUpForm = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: initialPhase === "signup" && initialEmail ? initialEmail : "",
      password: "",
      confirm: "",
      termsAgreement: false,
    },
  });

  const signUpPassword = signUpForm.watch("password");
  const signUpConfirm = signUpForm.watch("confirm");
  const termsAccepted = signUpForm.watch("termsAgreement");

  useEffect(() => {
    if (signUpForm.formState.touchedFields.password) {
      signUpForm.trigger("password");
    }
  }, [signUpPassword, signUpForm.formState.touchedFields.password, signUpForm]);

  useEffect(() => {
    if (signUpForm.formState.touchedFields.confirm) {
      signUpForm.trigger("confirm");
    }
  }, [
    signUpConfirm,
    signUpPassword,
    signUpForm.formState.touchedFields.confirm,
    signUpForm,
  ]);

  const showRequirements = passwordFieldActive || signUpPassword.length > 0;

  // ─── Handlers ───

  const fireOtcAndNavigate = async (
    targetEmail: string,
    authMethods?: string[],
  ) => {
    setCodeLoading(true);
    try {
      await initiateOtcAuth(targetEmail);
      onCodePath(targetEmail, authMethods);
    } catch {
      toast.error("Something went wrong. Please try again.");
      setCodeLoading(false);
    }
  };

  const onEmailNext = async (data: IdentifyFormValues) => {
    setEmail(data.email);
    try {
      const { exists, authMethods } = await checkEmailMutation({
        data: { email: data.email },
      });

      if (!exists) {
        if (codeFirst) {
          await fireOtcAndNavigate(data.email, authMethods);
        } else {
          signUpForm.setValue("email", data.email);
          setPhase("signup");
        }
      } else if (authMethods.includes("password")) {
        setPhase("password");
      } else {
        await fireOtcAndNavigate(data.email, authMethods);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const onPasswordContinue = async (data: PasswordFormValues) => {
    try {
      await loginUser(email, data.password, true);
      onSuccess();
    } catch {
      toast.error("Incorrect email or password. Please try again.");
    }
  };

  const onPasswordSignUp = async (data: SignUpFormValues) => {
    const signUpDto: SignUpDto = {
      email: data.email,
      password: data.password,
    };
    try {
      await signUpUser(signUpDto, true);
      onSuccess();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCodePath = async () => {
    const currentEmail =
      phase === "email" ? emailForm.getValues("email") : email;
    await fireOtcAndNavigate(currentEmail);
  };

  const handleBackToEmail = () => {
    setPhase("email");
    emailForm.setValue("email", email);
  };

  // ─── Phase Renders ───

  if (phase === "signup") {
    return (
      <SignupPhase
        signUpForm={signUpForm}
        onSubmit={onPasswordSignUp}
        onCodePath={handleCodePath}
        onBack={handleBackToEmail}
        compact={compact}
        hideHeading={hideHeading}
        codeLoading={codeLoading}
        signUpPassword={signUpPassword}
        showRequirements={showRequirements}
        termsAccepted={termsAccepted}
        onPasswordFieldFocus={() => setPasswordFieldActive(true)}
      />
    );
  }

  if (phase === "password") {
    return (
      <PasswordPhase
        email={email}
        passwordForm={passwordForm}
        onSubmit={onPasswordContinue}
        onCodePath={handleCodePath}
        onBack={handleBackToEmail}
        onForgotPassword={() => onForgotPassword(email)}
        compact={compact}
        hideHeading={hideHeading}
        codeLoading={codeLoading}
      />
    );
  }

  // ─── Email Phase ───
  return (
    <Flex
      className={`w-full ${compact ? "gap-4" : "gap-4 sm:gap-6 md:gap-8"}`}
      direction="column"
      aria-labelledby="identify-form-title"
    >
      {!hideHeading && (
        <div className="space-y-2 text-center">
          <Heading
            id="identify-form-title"
            as="h2"
            className={headingClass(compact)}
          >
            Welcome to Lutely
          </Heading>
        </div>
      )}

      <Form.Root
        className={`flex flex-col ${compact ? "gap-3" : "gap-4 sm:gap-6"}`}
        onSubmit={emailForm.handleSubmit(onEmailNext)}
        noValidate
      >
        <div className="space-y-3">
          <Text size="2" className="text-gray-8">
            Enter your email to sign in or get started
          </Text>
          <ControlledInputField
            name="email"
            control={emailForm.control}
            label="Email"
            type="email"
            placeholder="Email"
            required
            autoComplete="username"
            data-testid="auth-email-input"
          />
        </div>

        <Form.Submit asChild>
          <Button
            loading={emailForm.formState.isSubmitting}
            type="submit"
            radius="full"
            className="h-[40px]"
            data-testid="auth-next-button"
          >
            Next
          </Button>
        </Form.Submit>

        <SocialAuthButtons lastMethod={lastAuth?.method} />

        <Text
          size="1"
          className="text-gray-8 text-center text-[12px] leading-[1.4]"
        >
          By continuing, you agree to our{" "}
          <Link
            to="/terms"
            className="font-semibold text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy"
            className="font-semibold text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>
        </Text>
      </Form.Root>
    </Flex>
  );
}

export default IdentifyForm;
